import { useQuery } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import { useCallback, useMemo, useState } from 'react'
import { PiPlus } from 'react-icons/pi'
import { DataTable } from '../../../components/DataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import * as S from '../../../components/Tables/CommonTable.styles'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { DescriptionCell } from 'components/Tables/CommonTableCells/DescriptionCell'
import { InitialsAvatar } from 'components/Tables/CommonTableCells/InitialsAvatar'
import { TextPill } from 'components/FbUI/TextPill'
import {
  ActionsDropdown,
  createEditAction,
  createCopyAction,
  createToggleActiveAction,
  createDeleteAction,
} from 'components/Buttons/ActionsDropdown'
import FbButton from 'components/FbUI/FbButton'
import { CreateAutomationModal } from './AutomationModal/CreateAutomationModal'
import { EditAutomationModal } from './AutomationModal/EditAutomationModal'
import { CopyAutomationModal } from './AutomationModal/CopyAutomationModal'
import { Automation } from 'models/automation'
import useApiService from 'services/api'
import { toast } from 'react-toastify'
import confirm from 'components/dialogConfirm'
import { DataTableProvider } from 'components/DataTable/DataTableContext'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { TableSearch } from 'components/Filters/TableSearch'
import { FilterIdentifier } from 'models/saved_view'

const PAGE_SIZE = 20
const TABLE_KEY = 'automations-table'
const FILTER_IDENTIFIER: FilterIdentifier = 'AUTOMATIONS_TABLE'

export function AutomationsTable() {
  return (
    <TableLinkStyleProvider variant="plain">
      <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <AutomationsTableInner />
      </div>
    </TableLinkStyleProvider>
  )
}

function AutomationsTableInner() {
  const linkVariant = useTableLinkVariant()
  const isPlain = linkVariant === 'plain'
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAutomationId, setEditingAutomationId] = useState<number | null>(
    null
  )
  const [copyingAutomationId, setCopyingAutomationId] = useState<number | null>(
    null
  )

  const api = useApiService()
  const automationsFilterStore = filterStoreRepo.getStore(FILTER_IDENTIFIER)
  const { page, setPage } = automationsFilterStore()
  const { sorting } = dataTableSortingStoreRepo.getStore(TABLE_KEY)()

  const filtersAsParams = useFilterParams(automationsFilterStore)

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}
    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }
    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filtersAsParams,
      ...sortParams,
    }),
    [filtersAsParams, sortParams]
  )

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['automations', page, filterAndSortParams],
    queryFn: async ({ signal }) => {
      return await api.getAutomations(
        {
          page,
          limit: PAGE_SIZE,
          ...filterAndSortParams,
        },
        signal
      )
    },
  })

  const handleDelete = useCallback(
    async (automationId: number) => {
      const confirmed = await confirm(
        'Are you sure you want to delete this automation?',
        'Delete Automation',
        'This action cannot be undone.',
        { confirmLabel: 'Delete', buttonVariant: 'danger' }
      )

      if (!confirmed) {
        return
      }

      try {
        await api.deleteAutomation(automationId)
        toast.success('Automation deleted successfully')
        refetch()
      } catch (err) {
        console.error('Failed to delete automation:', err)
        toast.error('Failed to delete automation')
      }
    },
    [api, refetch]
  )

  const handleToggleActive = useCallback(
    async (automationId: number, currentActiveState: boolean) => {
      try {
        await api.partialUpdateAutomation(automationId, {
          is_active: !currentActiveState,
        })
        toast.success(
          `Automation ${!currentActiveState ? 'enabled' : 'disabled'} successfully`
        )
        refetch()
      } catch (err) {
        console.error('Failed to update automation:', err)
        toast.error('Failed to update automation')
      }
    },
    [api, refetch]
  )

  const columns = useMemo<ColumnDef<Automation, any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Automation Name',
        size: 300,
        meta: isPlain ? { isFirstColumn: true } : undefined,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 300,
        cell: (info) => <DescriptionCell value={info.getValue() as string} />,
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        size: 140,
        cell: (info) => {
          if (info.getValue()) {
            if (isPlain) {
              return (
                <TextPill textColor="#15803d" backgroundColor="#dcfce7">
                  Active
                </TextPill>
              )
            }
            return (
              <TextPill textColor="#13462C" backgroundColor="#D1E8D5">
                Active
              </TextPill>
            )
          } else {
            if (isPlain) {
              return (
                <TextPill textColor="#374151" backgroundColor="#f3f4f6">
                  Disabled
                </TextPill>
              )
            }
            return (
              <TextPill textColor="#464646" backgroundColor="#E3E1DA">
                Disabled
              </TextPill>
            )
          }
        },
      },
      {
        id: 'created_by',
        accessorFn: (row) =>
          `${row.created_by.first_name} ${row.created_by.last_name}`,
        header: 'Created By',
        size: 200,
        cell: (info) => {
          const user = info.row.original.created_by
          const initials =
            `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase()
          const fullName =
            `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
          if (isPlain) {
            return (
              <div className="flex items-center gap-2.5">
                <InitialsAvatar initials={initials} />
                <span className="truncate">{fullName}</span>
              </div>
            )
          }
          return fullName
        },
      },
      {
        accessorKey: 'created',
        header: 'Date Created',
        size: 160,
        cell: (info) => {
          const date = new Date(info.getValue())
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        },
      },
      {
        id: 'actions',
        header: isPlain
          ? () => <div className="w-full text-center">Actions</div>
          : 'Actions',
        size: isPlain ? 70 : 100,
        meta: {
          isLastColumn: true,
          headerDisableOrdering: true,
          rightAlign: !isPlain,
        },
        cell: (info) => (
          <ActionsDropdown
            className={isPlain ? '!w-full justify-center' : undefined}
            actions={[
              createEditAction(() => {
                setEditingAutomationId(info.row.original.id)
              }),
              createCopyAction(() => {
                setCopyingAutomationId(info.row.original.id)
              }),
              createToggleActiveAction(
                () =>
                  handleToggleActive(
                    info.row.original.id,
                    info.row.original.is_active
                  ),
                info.row.original.is_active
              ),
              createDeleteAction(() => {
                handleDelete(info.row.original.id)
              }),
            ]}
          />
        ),
      },
    ],
    [handleDelete, handleToggleActive, isPlain]
  )

  return (
    <>
      <ColumnsStoreProvider tableKey={TABLE_KEY}>
        <DataTableProvider tableKey={TABLE_KEY}>
          <DataTableContainer className="h-full">
            <DataTable
              tableKey={TABLE_KEY}
              tableHeader={
                <S.SearchContainer>
                  <S.SearchContainerFilters>
                    <S.TableSearchContainer>
                      <TableSearch
                        filterStore={automationsFilterStore}
                        searchPlaceholder="Search automations..."
                        tableKey={TABLE_KEY}
                      />
                    </S.TableSearchContainer>
                    <S.TableButtonsContainer>
                      <FbButton
                        variant="primary"
                        size="sm"
                        onClick={() => setShowCreateModal(true)}
                        className={
                          isPlain
                            ? '!bg-brand-bright !text-white hover:!bg-brand-active'
                            : undefined
                        }
                      >
                        <PiPlus size={16} />
                        Create Automation
                      </FbButton>
                    </S.TableButtonsContainer>
                  </S.SearchContainerFilters>
                </S.SearchContainer>
              }
              data={data?.results || []}
              columns={columns}
              loading={isLoading || isFetching}
              isPaginationEnabled={true}
              paginationOptions={{
                page: page,
                totalRows: data?.count || 0,
                pageSize: PAGE_SIZE,
                setPage: setPage,
                isPaginationLoading: isFetching,
              }}
              sortableFields="all"
              virtualizeRows={false}
              stickyLastColumn={isPlain}
              enableColumnResizing
              rowHeight={44}
              headerHeight={40}
            />
          </DataTableContainer>
        </DataTableProvider>
      </ColumnsStoreProvider>

      <CreateAutomationModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        onAutomationCreated={refetch}
      />

      {editingAutomationId && (
        <EditAutomationModal
          show={!!editingAutomationId}
          handleClose={() => setEditingAutomationId(null)}
          automationId={editingAutomationId}
          onAutomationUpdated={refetch}
        />
      )}

      {copyingAutomationId && (
        <CopyAutomationModal
          show={!!copyingAutomationId}
          handleClose={() => setCopyingAutomationId(null)}
          automationId={copyingAutomationId}
          onAutomationCreated={refetch}
        />
      )}
    </>
  )
}
